import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchDockingRecords } from '../api/docking';
import type { MarketProject } from '../types/market';
import {
  DEFAULT_PROGRESS_FILTER,
  type DockingRecord,
  type ProgressFilter,
  type ProgressStatistics,
} from '../types/docking';
import {
  countProgressStatistics,
  filterProgressRecords,
  sortProgressRecords,
} from '../utils/progressHelpers';

export interface UseProgressResult {
  records: DockingRecord[];
  allRecords: DockingRecord[];
  loading: boolean;
  error: string | null;
  filter: ProgressFilter;
  setFilter: React.Dispatch<React.SetStateAction<ProgressFilter>>;
  statistics: ProgressStatistics;
  selectedRecordId: string | null;
  setSelectedRecordId: (id: string | null) => void;
  /** 按项目选中一条优先展示的进展（未完成优先） */
  selectForProject: (projectId: string | null) => void;
  selectedRecord: DockingRecord | null;
  mapProjects: MarketProject[];
}

export function useProgress(allProjects: MarketProject[]): UseProgressResult {
  const [allRecords, setAllRecords] = useState<DockingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<ProgressFilter>(DEFAULT_PROGRESS_FILTER);
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDockingRecords();
      setAllRecords(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : '项目进展加载失败');
      setAllRecords([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const projectsById = useMemo(() => {
    const map = new Map<string, MarketProject>();
    allProjects.forEach((p) => map.set(p.id, p));
    return map;
  }, [allProjects]);

  const records = useMemo(
    () =>
      sortProgressRecords(
        filterProgressRecords(allRecords, projectsById, filter),
      ),
    [allRecords, projectsById, filter],
  );

  const statistics = useMemo(
    () => countProgressStatistics(records),
    [records],
  );

  const mapProjects = useMemo(() => {
    const seen = new Set<string>();
    const list: MarketProject[] = [];
    for (const record of records) {
      if (seen.has(record.projectId)) continue;
      const project = projectsById.get(record.projectId);
      if (!project) continue;
      seen.add(record.projectId);
      list.push(project);
    }
    return list;
  }, [records, projectsById]);

  useEffect(() => {
    if (!selectedRecordId) return;
    const stillExists = allRecords.some((r) => r.id === selectedRecordId);
    if (!stillExists) setSelectedRecordId(null);
  }, [allRecords, selectedRecordId]);

  const selectedRecord = useMemo(() => {
    if (!selectedRecordId) return null;
    return allRecords.find((r) => r.id === selectedRecordId) ?? null;
  }, [allRecords, selectedRecordId]);

  const selectForProject = useCallback(
    (projectId: string | null) => {
      if (!projectId) {
        setSelectedRecordId(null);
        return;
      }
      const forProject = allRecords.filter((r) => r.projectId === projectId);
      if (forProject.length === 0) {
        setSelectedRecordId(null);
        return;
      }
      const best = sortProgressRecords(forProject)[0];
      setSelectedRecordId(best.id);
    },
    [allRecords],
  );

  return {
    records,
    allRecords,
    loading,
    error,
    filter,
    setFilter,
    statistics,
    selectedRecordId,
    setSelectedRecordId,
    selectForProject,
    selectedRecord,
    mapProjects,
  };
}
